import { ButtonVariant, ContentPosition, SECTION_HEIGHT, hexToRgba } from '../../Commons';
import { ButtonVariantStyles as ButtonVariantStylesType, GlobalStyles } from '@zeus/shared';
import { ButtonVariantStyles } from '../../../components/DynamicButton/DynamicButton.styles';
import { LocalStyles } from '../types';

interface ButtonConfig {
  variant?: {
    value?: string;
  };
}

interface OverlayStyles {
  overlayColor?: string;
  overlayOpacity?: number;
  blurValue?: number;
}

interface ImageCornerRadius {
  selectedImageBorder?: string;
}

// Button style utilities
export const createButtonStyles = (
  button: ButtonConfig,
  globalStyles: GlobalStyles,
  localStyles: LocalStyles,
  isDesktop: boolean
) => {
  const buttonVariant = button?.variant?.value || ButtonVariant.PRIMARY;
  const buttonVariantStyles = ButtonVariantStyles[buttonVariant as keyof typeof ButtonVariantStyles];
  return {
    textDecoration: 'none',
    ...buttonVariantStyles,
    ...globalStyles.buttonVariants?.[buttonVariant as keyof ButtonVariantStylesType],
    ...localStyles.buttonVariants?.[buttonVariant as keyof ButtonVariantStylesType],
    borderRadius: `${localStyles.buttonRadius ?? globalStyles.buttonRadius}px`,
    fontFamily: globalStyles?.bodyText?.fontFamily,
  };
};

export const createSecondaryButtonStyles = (
  secondaryButton: ButtonConfig,
  globalStyles: GlobalStyles,
  localStyles: LocalStyles,
  isDesktop: boolean
) => {
  const buttonVariant = secondaryButton?.variant?.value || ButtonVariant.PRIMARY;
  const secondaryButtonVariantStyles = ButtonVariantStyles[buttonVariant as keyof typeof ButtonVariantStyles];

  return {
    textDecoration: 'none',
    ...secondaryButtonVariantStyles,
    ...globalStyles.buttonVariants?.[buttonVariant as keyof ButtonVariantStylesType],
    ...localStyles.secondaryButtonVariants?.[buttonVariant as keyof ButtonVariantStylesType],
    borderRadius: `${localStyles.buttonRadius ?? globalStyles.buttonRadius}px`,
    fontFamily: globalStyles?.bodyText?.fontFamily,
  };
};

// Section height utilities
export const createSectionHeightStyles = (
  sectionHeight: string,
  isDesktop: boolean,
  customHeight: number,
  builderMode?: boolean,
  designPreview?: boolean,
  mobileHeight = '30vh'
) => {
  if (sectionHeight === SECTION_HEIGHT.CUSTOM && customHeight) {
    const height = designPreview ? '80vh' : `${customHeight}px`;
    return {
      minHeight: height,
      height: 'auto',
    };
  }

  if (sectionHeight === SECTION_HEIGHT.FULL_SCREEN_HEIGHT) {
    // Full screen height varies in builder mode vs published mode
    // In builder mode, account for editor container; in published mode, use full viewport
    const height = designPreview ? '80vh' : '100vh';
    return {
      minHeight: height,
      height: 'auto',
    };
  }

  // Default height behavior
  if (sectionHeight === SECTION_HEIGHT.DEFAULT) {
    if (!isDesktop) {
      return {
        minHeight: mobileHeight,
        height: 'auto',
      };
    }
    const height = designPreview ? '80vh' : '75vh';
    return {
      minHeight: height,
      height: 'auto',
    };
  }

  // Default height behavior - auto-adjusting based on content (similar to About Us)
  return {
    minHeight: 'auto',
    height: 'auto',
  };
};

// Background overlay utilities
export const createBackgroundOverlayStyles = (overlayStyles: OverlayStyles) => {
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    fontSize: 0,
    pointerEvents: 'none', // Allow clicks to pass through to carousel buttons
    zIndex: 1, // Above background media (zIndex: 0) but below carousel buttons (zIndex: 10)
    '::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      fontSize: 0,
      background: hexToRgba(overlayStyles?.overlayColor || '#000000', overlayStyles?.overlayOpacity || 0),
      pointerEvents: 'none',
      zIndex: 1, // Above background media
      backdropFilter: `blur(${overlayStyles?.blurValue || 0}px)`,
      WebkitBackdropFilter: `blur(${overlayStyles?.blurValue || 0}px)`, // for Safari support
    },
  };
};

// Content position utilities
export const createPositionStyles = (position: ContentPosition = ContentPosition.LEFT) => {
  const positionStyles = {
    [ContentPosition.LEFT]: {
      alignItems: 'flex-start',
      textAlign: 'left' as const,
      justifyContent: 'flex-start',
      marginLeft: '0',
      marginRight: 'auto',
      paddingLeft: '2rem',
    },
    [ContentPosition.CENTER]: {
      alignItems: 'center',
      textAlign: 'center' as const,
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      paddingLeft: '2rem',
      paddingRight: '2rem',
    },
    [ContentPosition.RIGHT]: {
      alignItems: 'flex-end',
      textAlign: 'right' as const,
      justifyContent: 'flex-end',
      marginLeft: 'auto',
      marginRight: '0',
      paddingRight: '2rem',
    },
  };

  return positionStyles[position];
};

// Image border radius utilities
export const createImageBorderRadius = (
  globalStyles: ImageCornerRadius,
  isDesktop: boolean,
  IMAGE_CORNER_RADIUS_SOFT: number,
  IMAGE_CORNER_RADIUS_SOFT_MOBILE: number
) => {
  if (globalStyles.selectedImageBorder === 'sharpCorners') {
    return 0; // IMAGE_CORNER_RADIUS_SHARP
  }
  return isDesktop ? IMAGE_CORNER_RADIUS_SOFT : IMAGE_CORNER_RADIUS_SOFT_MOBILE;
};
